import React, { useEffect, useState } from 'react';
import { Effect } from 'effect';
import { HttpClientResponse } from '@effect/platform';
import { View, Text, useTheme, PhoneNumberField } from '@aws-amplify/ui-react';
import { I18n } from 'aws-amplify';
import { ThirdPartyApi } from 'services/api';
import { GetCountryByIpResponse } from 'services/schemata/response';
import { getFormattedCountryCodes, extractDialCode, getFormattedDialCodeByUserLocale } from 'utils/countryCodes';
import { useLocaleState } from 'contexts/localeContext';
import { useRuntime } from '../../../effect/useRuntime';
import ActionButtons from './ActionButtons';
import FormHeader from './FormHeader';
import PhoneVerificationInstructions from './PhoneVerificationInstructions';

const PhoneRegistration = ({ onUpdatePhone, onSkip, onBackToSignIn, validationError }) => {
  const { tokens } = useTheme();
  const { rtl } = useLocaleState();
  const [phoneValue, setPhoneValue] = useState('');
  const [geoIpCountryCode, setGeoIpCountryCode] = useState('');
  const [dialCode, setDialCode] = useState('');
  const [isFormValid, setIsFormValid] = useState(false);

  const runtime = useRuntime();

  useEffect(() => {
    ThirdPartyApi.use((service) => service.getCountry()).pipe(
      Effect.timeout('2 seconds'),
      Effect.flatMap(HttpClientResponse.schemaBodyJson(GetCountryByIpResponse)),
      Effect.catchTag('ParseError', (error) => {
        console.error('Schema parsing failed for country data:', error.message);
        return Effect.void;
      }),
      Effect.tap((json) => {
        const code = json?.country || 'IL';
        setGeoIpCountryCode(code);
      }),
      Effect.catchAll((error) => {
        console.error('Error while fetching country data:', error);
        setGeoIpCountryCode('IL');
        return Effect.void;
      }),
      runtime.runPromise,
    );
  }, [runtime.runPromise]);

  useEffect(() => {
    const code = getFormattedDialCodeByUserLocale(rtl, geoIpCountryCode);
    setDialCode(code);
  }, [rtl, geoIpCountryCode]);

  const validateForm = React.useCallback(
    (value = phoneValue) => {
      // International phone numbers are typically 7-12 digits (excluding country code)
      const isValid = value.length >= 7 && value.length <= 12;
      setIsFormValid(isValid);
      return isValid;
    },
    [phoneValue],
  );

  const handlePhoneNumberChange = (e) => {
    // Allow digits only, no spaces or formatting as component doesnt handle this
    const filteredValue = e.target.value.replace(/[^0-9]/g, '');
    setPhoneValue(filteredValue);
    validateForm(filteredValue);
  };

  const handleDialCodeChange = (e) => {
    setDialCode(e.target.value);
  };

  const updateNumber = () => {
    if (dialCode && phoneValue) {
      const rawDialCode = extractDialCode(dialCode);
      onUpdatePhone(rawDialCode + phoneValue);
    }
  };

  const buttons = [
    {
      onClick: updateNumber,
      variation: 'primary',
      size: 'default',
      isFullWidth: true,
      disabled: !isFormValid,
      style: { textTransform: 'uppercase' },
      text: I18n.get('phoneVerification.step1.sendButton'),
    },
    {
      onClick: onSkip,
      variation: 'default',
      size: 'default',
      isFullWidth: true,
      backgroundColor: tokens.colors.background.primary,
      color: tokens.colors.brand.primary[80],
      borderColor: tokens.colors.brand.primary[80],
      borderWidth: '1px',
      style: { textTransform: 'uppercase' },
      text: I18n.get('phoneVerification.skipButton'),
    },
    {
      onClick: onBackToSignIn,
      variation: 'link',
      size: 'default',
      isFullWidth: true,
      color: tokens.colors.brand.primary[80],
      style: { textTransform: 'uppercase' },
      text: I18n.get('phoneVerification.step1.backButton'),
    },
  ];

  return (
    <View padding={tokens.space.large}>
      {/* Logo and Header */}
      <FormHeader titleTranslationKey="phoneVerification.step1.title" />

      {/* Description */}
      <PhoneVerificationInstructions
        block1TranslationKey="phoneVerification.step1.block1"
        // block2TranslationKey="phoneVerification.step1.block2"
      />

      {/* Phone Input Form */}
      {geoIpCountryCode && dialCode && (
        <View marginTop={tokens.space.large}>
          <div dir="ltr" className={rtl ? 'phone-field-rtl' : 'phone-field-ltr'}>
            <PhoneNumberField
              value={phoneValue}
              onChange={handlePhoneNumberChange}
              defaultDialCode={dialCode}
              onDialCodeChange={handleDialCodeChange}
              dialCodeList={getFormattedCountryCodes(rtl)}
              placeholder={I18n.get('phoneVerification.step1.phone.placeholder')}
              size="medium"
              type="tel"
              inputMode="numeric"
              pattern="[0-9]{7,12}"
              maxLength="12"
              hasError={!!validationError}
              errorMessage={validationError || ''}
            />
          </div>
          <Text
            fontSize={tokens.fontSizes.xs}
            color={tokens.colors.font.tertiary}
            textAlign={rtl ? 'right' : 'left'}
            padding={tokens.space.xs}
          >
            {I18n.get('footer.step2.changeNumber')}
          </Text>
        </View>
      )}

      <ActionButtons buttons={buttons} />
    </View>
  );
};

export default PhoneRegistration;
